export interface GetRewardResponse {
	amountEarned: number;
	messageEngine: string;
	engineResponse: {
		type: string;
		params: {
			points: number;
			currentLadderStep: number;
			[key: string]: unknown;
		};
	};
}

export interface RequestEnginePayload {
	requestEngine: {
		nameRule: string;
		fact: {
			dynamicFact: {
				factNameDynamic: string;
				token: string;
				urlDynamicFact: string;
			};
			factNameInputs: string;
			calculateFact: {
				factNameCalculate: string;
			};
		};
		data: {
			accountId: string;
			eventType: string;
			event: string;
			currentLadderStep: number;
			organizationId: string;
			[key: string]: unknown;
		};
	};
}

export enum EventTypeEnum {
	JOURNAL = "JOURNAL",
	STEP_COMPLETION = "STEP_COMPLETION",
	GOAL_COMPLETION = "GOAL_COMPLETION",
	SUPERVISION_ACTIVITY = "SUPERVISION_ACTIVITY",
	IN_HOUSE_SERVICE = "IN_HOUSE_SERVICE",
	EXTERNAL_PROVIDER_SERVICE = "EXTERNAL_PROVIDER_SERVICE",
	REDEEMED = "REDEEMED",
}

export enum RewardConfigurationType {
	FixedLadder = "FIXED_LADDER",
	ProgrammableLadder = "PROGRAMMABLE_LADDER",
}

export interface FixedLadderConfiguration {
	index: number;
	points: number;
}

export interface ProgrammableLadderConfiguration {
	startValue: number;
	stepValue: number;
	maxValue: number;
	resetValue: number;
}

export interface InvalidResult {
	invalidResultType?: string;
	rewardForInvalid?: string;
	resetRuleType?: string;
	specificIndex?: number;
}

export interface EventResultCondition {
	relativePosition: number; // -1 for last event, -2 for second to last, etc.
	checkType: string; // "IS_VALID", "IS_INVALID", "EQUALS_RESULT_ID", "NOT_EQUALS_RESULT_ID"
	resultId?: string; // Specific result ID to check against (for EQUALS/NOT_EQUALS)
	description?: string; // Human-readable description of the rule
	targetStep?: number; // Step to reset to (for certain reset types)
	action?: string; // "RESET_TO_STEP", "PUSH_STEP"
}

export interface RecoveryConfiguration {
	enabled: boolean;
	type?: string; // "PUSH_STEP", "REPLACE_STEP", or "LAST_STEP"
	resetToStep?: number; // Which step to reset to (default: 1)
	recoverInSteps?: number; // How many visits to catch back up to normal (for PUSH_STEP and LAST_STEP)
	customRecoveryLadderStart?: number; // Minimum payment tier (1-indexed) before recovery applies (LAST_STEP only)
	description?: string;
}

export interface CustomLadderReset {
	enabled: boolean;
	conditions: EventResultCondition[]; // Conditions that trigger a reset
	validResultIds: string[]; // Result IDs considered "valid" (e.g., ["NEG", "NEGATIVE"])
	invalidResultIds: string[]; // Result IDs considered "invalid" (e.g., ["POS", "POSITIVE"])
	recovery?: RecoveryConfiguration; // Recovery configuration after reset (Push Step vs Replace Step)
	maxHistoryCheck?: number; // How many previous events to check (default: all)
}

export interface NeutralResetRule {
	resultId: string; // Result ID to track (e.g., "Dilute", "Missing")
	consecutiveCount: number; // Number of consecutive occurrences to trigger reset
}

export interface CustomNeutralReset {
	enabled: boolean;
	rules: NeutralResetRule[]; // Rules for detecting repeating neutral patterns
	description?: string;
}

export interface RewardsEngine {
	_id?: string; // Optional for cloning
	accountId: string;
	programId?: string;
	eventType: string;
	event: string;
	rewardConfigurationType: RewardConfigurationType;
	rewardType?: "MONEY" | "FISHBOWL";
	rewardBasedOnResult?: boolean;
	validValue?: string;
	invalidValue?: string;
	orgUnitIds?: string[];
	fixedLadderConfiguration?: FixedLadderConfiguration[];
	programmableLadderConfiguration?: ProgrammableLadderConfiguration[];
	invalidResult?: InvalidResult;
	customLadderReset?: CustomLadderReset;
	customNeutralReset?: CustomNeutralReset;
	maxRewardAmount?: number;
	maxOccurrences?: number;
	createdAt?: string; // Optional for cloning
	updatedAt?: string; // Optional MongoDB timestamp
	createdBy: string;
	isDeleted?: boolean; // Optional
	__v?: number; // Optional MongoDB version key
}

// Fishbowl configuration types (aligned with backend)
export enum FishbowlType {
	PHYSICAL = "PHYSICAL",
	DIGITAL = "DIGITAL",
}

export interface PrizeDistribution {
	amount: number;
	probability: number;
	displayMessage: string;
	// SIMS-9080 (portal D-95) — options in display order, present only with two or more;
	// displayMessage mirrors option 1. Never carries an index or number (D5).
	displayMessages?: string[];
}

// SIMS-9077 — how a digital fishbowl's draws are presented. Deliberately NOT a new FishbowlType:
// fishbowlType stays exactly PHYSICAL/DIGITAL, which is what reporting and the correction path
// key on. Mirrors FishbowlPresentation in the backend's models/TreatmentPlan.ts.
export enum FishbowlPresentation {
	LIST = "LIST",
	GAME = "GAME",
}

export interface FishbowlCustomConfig {
	fishbowlType: FishbowlType | "";
	prizeDistributions?: PrizeDistribution[];
	// SIMS-9077 AC2 — OPTIONAL and it stays optional: every configuration saved before this
	// release has no presentation key, and absence resolves to LIST on READ
	// (resolveFishbowlPresentation) rather than being written back.
	presentation?: FishbowlPresentation;
}

/**
 * SIMS-9073 (portal Q-17) — the coordinates of ONE fishbowl draw.
 *
 * Keyed on the VISIT, not on a `udtResultId`: the draws are played before the UDT result
 * exists (DateCard holds the visit in React state until submit), so the id the original Q-12
 * route wanted is minted after the draws it was meant to persist. These five values are known
 * both at visit entry and when correcting an already-recorded visit.
 */
export interface FishbowlDrawCoordinates {
	week: number;
	visitNumber: number;
	treatmentConfigName: string;
	/** 0 for the original visit; incremented by each correction. */
	generation: number;
	/** 0-based position within this visit's draws. */
	drawIndex: number;
}

/** One resolved draw, exactly as the server decided it and as the client must render it. */
export interface FishbowlDrawResponse {
	amount: number;
	/** Already expanded — the client never substitutes [AMOUNT] itself any more. */
	message: string;
	probability: number;
	drawnAt: string;
}

/** SIMS-9086 — a draw already played for a visit, with its position (GET …/fishbowl-draws). */
export interface FishbowlVisitDraw extends FishbowlDrawResponse {
	drawIndex: number;
}
